import React from "react";
import { VeriDateRangeInputProps } from "./VeriDateRangeInput.types";
import { useBooleanState } from "../../../hooks";
import { FormControlWrapper } from "../FormControlWrapper";
import { Box, HStack } from "@chakra-ui/react";
import { CalendarIcon, CloseIcon } from "../../../icons";
import { cx } from "@chakra-ui/utils";
import { usePopper } from "react-popper";
import { VeriDatePicker } from "../../datepicker/VeriDatePicker";
import {
  dateToYYYYMMDDNoIso,
  formatDateMMDDYYYY,
  isValidInputDateDob,
  isValidYYYYMMDDDate,
} from "../../../utils/dates";
import { RangeDateInput } from "./RangeDateInput";
import { PickerArrow } from "./PickerArrow";
import { Button } from "../../common";
import { format, parse } from "date-fns";

export const VeriDateRangeInput = React.forwardRef<
  HTMLInputElement,
  VeriDateRangeInputProps
>(
  (
    {
      useCalendarIcon = true,
      onChange,
      startDate: valueStartDate,
      endDate: valueEndDate,
      fromPlaceholder,
      toPlaceholder,
      showCancelButton,
      ...rest
    },
    ref,
  ) => {
    const [startDate, setStartDate] = React.useState<string | null>("");
    const [endDate, setEndDate] = React.useState<string | null>("");

    const isPickerOpen = useBooleanState(false);

    const [rootBoxRef, setRootBoxRef] = React.useState<HTMLDivElement | null>(
      null,
    );
    const [pickerBoxRef, setPickerBoxRef] =
      React.useState<HTMLDivElement | null>(null);

    const [arrowBoxRef, setArrowBoxRef] = React.useState<HTMLDivElement | null>(
      null,
    );

    const {
      styles,
      attributes,
      state: popperState,
    } = usePopper(rootBoxRef, pickerBoxRef, {
      modifiers: [
        {
          name: "offset",
          enabled: true,
          options: {
            offset: [0, 6],
          },
        },
        { name: "arrow", options: { element: arrowBoxRef } },
      ],
    });

    const onInputFocus = () => {
      isPickerOpen.true();
    };

    const handleCancelDates = () => {
      setStartDate("");
      setEndDate("");

      onChange?.({
        startDate: null,
        endDate: null,
      });

      isPickerOpen.false();
    };

    const _className = cx("chakra-input", rest.className);

    const onOutsideClick = React.useCallback(
      (event) => {
        if (rootBoxRef && !rootBoxRef.contains(event.target)) {
          isPickerOpen.false();
        }
      },
      [rootBoxRef],
    );

    React.useEffect(() => {
      window.document.addEventListener("click", onOutsideClick, true);

      return () => {
        window.document.removeEventListener("click", onOutsideClick, true);
      };
    }, [rootBoxRef]);

    React.useEffect(() => {
      if (valueStartDate && isValidYYYYMMDDDate(valueStartDate)) {
        //setStartDate(formatDateMMDDYYYY(valueStartDate) || "");
        setStartDate(
          format(
            parse(valueStartDate, "yyyy-MM-dd", new Date()),
            "MM/dd/yyyy",
          ) || "",
        );
      }
      if (valueEndDate && isValidYYYYMMDDDate(valueEndDate)) {
        setEndDate(formatDateMMDDYYYY(valueEndDate) || "");
      }
    }, [valueStartDate, valueEndDate]);

    return (
      <FormControlWrapper
        inputLeft={useCalendarIcon ? <CalendarIcon boxSize={4} /> : undefined}
        {...rest}
      >
        <Box
          className={_className}
          border={"1px solid"}
          borderColor={"border.input"}
          borderRadius={4}
          w={"100%"}
          pl={useCalendarIcon ? 6 : undefined}
          ref={setRootBoxRef}
        >
          <HStack>
            <RangeDateInput
              name={rest.name}
              ref={ref}
              value={startDate || ""}
              onChange={setStartDate}
              onFocus={onInputFocus}
              placeholder={fromPlaceholder}
            />
            <Box>-</Box>
            <RangeDateInput
              name={rest.name}
              ref={ref}
              value={endDate || ""}
              onChange={setEndDate}
              onFocus={onInputFocus}
              placeholder={toPlaceholder}
            />
            {showCancelButton && (
              <Button
                visibility={startDate || endDate ? "visible" : "hidden"}
                pr={2}
                py={0}
                onClick={handleCancelDates}
                empty
              >
                <CloseIcon />
              </Button>
            )}
          </HStack>
          {isPickerOpen.state && (
            <Box
              ref={setPickerBoxRef}
              style={{ ...styles.popper, zIndex: undefined }}
              zIndex={"popover"}
              {...attributes.popper}
            >
              {popperState?.placement === "bottom" ? (
                <PickerArrow
                  ref={setArrowBoxRef}
                  style={{
                    ...styles.arrow,
                    transform:
                      (styles.arrow?.transform || "") + " rotate(225deg)",
                  }}
                />
              ) : undefined}
              <Box
                boxShadow={2}
                background={"white"}
                border={"1px solid"}
                borderColor={"grey.1000"}
                borderRadius={4}
                px={3}
                py={2}
                position={"relative"}
              >
                <VeriDatePicker
                  rangeStartDate={
                    startDate && isValidInputDateDob(startDate)
                      ? dateToYYYYMMDDNoIso(startDate)
                      : ""
                  }
                  rangeEndDate={
                    endDate && isValidInputDateDob(endDate)
                      ? dateToYYYYMMDDNoIso(endDate)
                      : ""
                  }
                  onRangeChange={(start, end) => {
                    setStartDate(start ? dateToYYYYMMDDNoIso(start) || "" : "");
                    setEndDate(end ? dateToYYYYMMDDNoIso(end) || "" : "");

                    onChange?.({
                      startDate: start || null,
                      endDate: end || null,
                    });
                  }}
                  isRangePicker
                />
              </Box>
              {popperState?.placement === "top" ? (
                <PickerArrow
                  ref={setArrowBoxRef}
                  style={{
                    ...styles.arrow,
                    transform:
                      (styles.arrow?.transform || "") + " rotate(45deg)",
                  }}
                />
              ) : undefined}
            </Box>
          )}
        </Box>
      </FormControlWrapper>
    );
  },
);

VeriDateRangeInput.displayName = "VeriDateRangeInput";
